<script setup lang="ts">
import { computed, ref } from 'vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import VSearch from '@shared/component/VSearch/VSearch.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useWatchErrorHandler } from '@errors/useErrorHandler';

import { useMaatschappijDocumentenGetPolisvoorwaarden } from '@services/documenten-public/orval-api/maatschappij-documenten/maatschappij-documenten';

const { data, error, isPending } = useMaatschappijDocumentenGetPolisvoorwaarden({
  query: { select: (res) => res.result },
});

useWatchErrorHandler(error);

const zoekterm = ref('');
const geselecteerdeMaatschappij = ref<string | null>(null);

const gefilterdeData = computed(() => {
  const maatschappijen = data.value ?? [];
  const term = zoekterm.value.toLowerCase();

  if (term.length < 2) {
    return maatschappijen;
  }

  return maatschappijen
    .map((maatschappij) => {
      const matchesMaatschappij = maatschappij.naam.toLowerCase().includes(term);

      if (matchesMaatschappij) {
        return maatschappij;
      }

      const gefilterdeVoorwaarden = maatschappij.voorwaarden.filter((v) =>
        v.bestandsnaam.toLowerCase().includes(term),
      );

      if (gefilterdeVoorwaarden.length === 0) {
        return null;
      }

      return {
        ...maatschappij,
        voorwaarden: gefilterdeVoorwaarden,
      };
    })
    .filter(Boolean) as typeof maatschappijen;
});

const actieveMaatschappij = computed(() => {
  const maatschappijen = gefilterdeData.value;
  if (maatschappijen.length === 0) return null;

  const gevonden = maatschappijen.find((m) => m.naam === geselecteerdeMaatschappij.value);
  return gevonden ?? maatschappijen[0];
});

const gesorteerdVoorwaarden = computed(() => {
  if (!actieveMaatschappij.value) return [];
  return [...actieveMaatschappij.value.voorwaarden].sort((a, b) =>
    a.bestandsnaam.localeCompare(b.bestandsnaam),
  );
});

function selecteerMaatschappij(naam: string) {
  geselecteerdeMaatschappij.value = naam;
}

const getDocumentUrl = (voorwaardeId: number) =>
  `/management/api/v1/documenten/maatschappijen/voorwaarden/${voorwaardeId}`;
</script>

<template>
  <div>
    <template v-if="isPending">
      <div class="flex gap-6">
        <div class="w-64 space-y-2">
          <VSkeleton v-for="i in 8" :key="i" height="md" />
        </div>
        <div class="flex-1">
          <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <VSkeleton v-for="i in 6" :key="i" height="lg" />
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="mb-4">
        <VSearch
          v-model="zoekterm"
          placeholder="Zoek polisvoorwaarden..."
          class="w-full max-w-xs"
        />
      </div>

      <div v-if="gefilterdeData.length > 0" class="flex gap-6">
        <div class="w-80 shrink-0">
          <nav class="space-y-1">
            <button
              v-for="maatschappij in gefilterdeData"
              :key="maatschappij.naam"
              type="button"
              :class="[
                'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors',
                actieveMaatschappij?.naam === maatschappij.naam
                  ? 'bg-white font-semibold text-primary shadow-sm ring-1 ring-grey-200'
                  : 'text-grey-700 hover:bg-grey-100',
              ]"
              @click="selecteerMaatschappij(maatschappij.naam)"
            >
              <span>{{ maatschappij.naam }}</span>
              <span
                :class="[
                  'rounded-full px-2 py-0.5 text-xs',
                  actieveMaatschappij?.naam === maatschappij.naam
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-grey-200 text-grey-600',
                ]"
              >
                {{ maatschappij.voorwaarden.length }}
              </span>
            </button>
          </nav>
        </div>

        <div class="min-w-0 flex-1">
          <div v-if="actieveMaatschappij" class="rounded-lg border border-grey-200 bg-white">
            <div class="border-b border-grey-200 bg-grey-50 px-4 py-3">
              <h2 class="font-semibold text-grey-800">
                {{ actieveMaatschappij.naam }}
              </h2>
              <p class="text-sm text-grey-500">
                {{ gesorteerdVoorwaarden.length }} document{{
                  gesorteerdVoorwaarden.length === 1 ? '' : 'en'
                }}
              </p>
            </div>

            <ul class="grid grid-cols-1 divide-y divide-grey-100 xl:grid-cols-2">
              <li
                v-for="voorwaarde in gesorteerdVoorwaarden"
                :key="voorwaarde.bestandId"
                class="hover:bg-grey-50"
              >
                <a
                  :href="getDocumentUrl(voorwaarde.voorwaardeId)"
                  target="_blank"
                  download
                  rel="noopener noreferrer"
                  class="flex items-center gap-3 px-4 py-3 text-primary hover:underline"
                >
                  <FontAwesomeIcon icon="far fa-file-pdf" class="text-red-500" />
                  {{ voorwaarde.bestandsnaam }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <p v-else-if="zoekterm" class="mt-6 text-center text-grey-500">
        Geen polisvoorwaarden gevonden voor "{{ zoekterm }}"
      </p>

      <p v-else class="mt-6 text-center text-grey-500">Geen polisvoorwaarden beschikbaar.</p>
    </template>
  </div>
</template>
